import { useEffect, useMemo, useRef, useState } from "react"

import { useSearchParams } from "react-router-dom"

import Icon from "@/components/Icon"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/AuthContext"
import { useFetchPeople } from "@/hooks/useFetchPeople"
import { usePerson } from "@/hooks/usePerson"
import { useSubTeams } from "@/hooks/useSubTeams"
import { Analytics, AnalyticsEvent } from "@/services/analytics"
import { Person } from "@/types/person"
import { FilterEntry, FilterType } from "@/utils/filtering"
import { formatPersonShortName } from "@/utils/personDisplay"

import { Spinner } from "../ui/spinner"
import PeopleDirectory from "./PeopleDirectory"
import PersonDetails from "./PersonDetails"
import CreateEmployeeDialog from "./dialogs/CreateEmployeeDialog"
import FilterBar from "./ui/FilterBar"

interface PeopleContentProps {
  personId?: string | null
}

const PeopleContent = ({ personId }: PeopleContentProps) => {
  const { user, claims } = useAuth()
  const { subTeamOptions } = useSubTeams()

  const [searchParams, setSearchParams] = useSearchParams()

  const searchQuery = searchParams.get("q") ?? (personId || "")

  const parsedFilters: FilterEntry[] = useMemo(() => {
    const param = searchParams.get("filters")
    if (!param) return []
    try {
      return JSON.parse(param)
    } catch {
      return []
    }
  }, [searchParams])

  const getFilterValue = (id: string): string => {
    const entry = parsedFilters.find((f) => f.id === id)
    if (!entry?.value.length) return ""
    return entry.value[0].value
  }

  const filterTypes = useMemo(() => {
    const types: Record<string, string> = {}
    for (const entry of parsedFilters) {
      if (entry.value.length > 0 && entry.value[0].filterType !== FilterType.Is) {
        types[entry.id] = entry.value[0].filterType
      }
    }
    return types
  }, [parsedFilters])

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useFetchPeople(
    {
      statusFilter: getFilterValue("status") || "all",
      searchQuery,
      functionAreaFilter: getFilterValue("area"),
      locationFilter: getFilterValue("location"),
      employerFilter: getFilterValue("employer"),
      subTeamFilter: getFilterValue("subTeam"),
      sheriffTypeFilter: getFilterValue("sheriffType"),
      personIdFilter: getFilterValue("personId"),
      emailFilter: getFilterValue("email"),
      postcodeFilter: getFilterValue("postcode"),
      stickyLocationFilter: getFilterValue("stickyLocation"),
      driverManagerTeamFilter: getFilterValue("driverManagerTeam"),
      contractStartDateFilter: getFilterValue("contractStartDate"),
      contractEndDateFilter: getFilterValue("contractEndDate"),
      hpdPoolFilter: getFilterValue("hpdPool"),
      payBasisFilter: getFilterValue("payBasis"),
      payRateFilter: getFilterValue("payRate"),
      contractTypeFilter: getFilterValue("contractType"),
      hasMultipleLocationsFilter: getFilterValue("hasMultipleLocations") as "Yes" | "No" | "",
      filterTypes,
      userSubTeams: user?.sub_teams || [],
    },
  )

  const handleSearch = (searchTerm: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (searchTerm) {
          next.set("q", searchTerm)
        } else {
          next.delete("q")
        }
        return next
      },
      { replace: true },
    )
  }

  const handleApplyFilters = (filters: FilterEntry[]) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (filters.length > 0) {
          next.set("filters", JSON.stringify(filters))
        } else {
          next.delete("filters")
        }
        return next
      },
      { replace: true },
    )
  }

  const handleFetchMore = () => {
    fetchNextPage()
  }

  const exportPeopleCsv = (people: Person[]) => {
    Analytics.track(AnalyticsEvent.PEOPLE_EXPORTED, {
      personIds: people.map((p) => p.person_id),
    })
    const headers = [
      "Person ID",
      "Status",
      "Depot",
      "Phone",
      "Email",
      "Sheriff Type",
      "Sheriff Areas",
      "Sticky Location",
    ]

    const escapeField = (value: string) => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    const rows = people.map((p) =>
      [
        p.person_id ? formatPersonShortName(p) : "",
        p.status ?? "",
        p.location?.join("; ") ?? "",
        p.phone_number ?? "",
        p.email ?? "",
        p.sheriff_type ?? "",
        p.sheriff_areas?.join("; ") ?? "",
        p.sticky_location ?? "",
      ].map(escapeField),
    )

    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "people-export.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  const allPeople = useMemo(() => data?.pages.flatMap((page) => page.people) || [], [data?.pages])

  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)
  const selectPerson = (person: Person) => setSelectedPersonId(person.id)
  const { data: selectedPerson, isError: selectedPersonFailed } = usePerson(selectedPersonId)
  const [createEmployeeOpen, setCreateEmployeeOpen] = useState(false)

  const canCreateEmployee = claims?.user_role === "Admin" && claims?.user_team === "Admin"
  const canExport =
    claims?.user_role === "Admin" &&
    (claims?.user_team === "Admin" ||
      claims?.user_team === "Central Operations" ||
      claims?.user_team === "Depot")

  const panelRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const primaryPerson = selectedPerson?.primary_person_id
    ? allPeople.find((p) => p.id === selectedPerson.primary_person_id) || null
    : null

  useEffect(() => {
    if (!selectedPersonId) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        contentRef.current?.contains(target)
      ) {
        setSelectedPersonId(null)
      }
    }
    document.addEventListener("click", handleClick)
    return () => document.removeEventListener("click", handleClick)
  }, [selectedPersonId])

  useEffect(() => {
    if (personId && allPeople.length > 0) {
      const foundPerson = allPeople.find((p) => p.person_id === personId)
      if (foundPerson) {
        setSelectedPersonId(foundPerson.id)
        setTimeout(() => {
          const element = document.querySelector(`[data-person-id="${personId}"]`)
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 100)
      }
    }
  }, [personId, allPeople])

  return (
    <div
      ref={contentRef}
      className="h-[calc(100vh-theme(spacing.6)-theme(spacing.6)-64px)] flex flex-col"
    >
      {isLoading && !isFetchingNextPage && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
          <Spinner size="lg" />
          <span className="ml-2 text-palette-jungleGreen700">Loading people data...</span>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <Icon name="warningCircleFilled" className="h-4 w-4" />
          <AlertDescription>
            Error loading people data: {error instanceof Error ? error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      )}

      <div className="h-[calc(100vh-theme(spacing.6)-theme(spacing.6)-64px)] flex flex-col">
        <div className="pb-4">
          <h2 className="text-lg font-semibold text-palette-jungleGreen900 mb-3">People</h2>
          <div className="flex flex-wrap gap-3 items-center">
            <FilterBar
              filters={parsedFilters}
              onApplyFilters={handleApplyFilters}
              initialSearch={searchQuery}
              onSearch={handleSearch}
              subTeamOptions={subTeamOptions}
              userRole={claims?.user_role}
            />
            <div className="flex items-center gap-3 ml-auto">
              {canExport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportPeopleCsv(allPeople)}
                  className="h-10 gap-2 rounded-full border-palette-jungleGreen200 px-5"
                >
                  <Icon name="downloadSimple" className="h-4 w-4 text-palette-jungleGreen700" />
                  Export
                </Button>
              )}
              {canCreateEmployee && (
                <Button
                  size="sm"
                  onClick={() => setCreateEmployeeOpen(true)}
                  className="h-10 rounded-full px-5 bg-palette-honeycomb500 text-palette-jungleGreen900 hover:bg-palette-honeycomb400"
                >
                  <Icon name="plus" className="mr-1 h-4 w-4 text-palette-jungleGreen900" />
                  Create HIVED employee
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-1 overflow-hidden bg-white rounded-lg shadow-sm">
          <div
            className={`flex flex-col overflow-hidden transition-all ${
              selectedPersonId ? "w-[40%]" : "w-full"
            }`}
          >
            <PeopleDirectory
              people={allPeople}
              selectedPersonId={selectedPersonId}
              onSelectPerson={selectPerson}
              onFetchMore={handleFetchMore}
              loading={isFetchingNextPage}
              hasMorePeople={hasNextPage}
            />
          </div>

          {selectedPersonId && (
            <div
              ref={panelRef}
              className="w-[60%] overflow-auto shadow-[-4px_0_12px_-2px_rgba(0,0,0,0.08)]"
            >
              {selectedPerson ? (
                <PersonDetails
                  person={selectedPerson}
                  primaryPerson={primaryPerson}
                  onSelectPerson={selectPerson}
                  onClose={() => setSelectedPersonId(null)}
                />
              ) : (
                <div className="flex justify-center p-8 text-palette-jungleGreen700">
                  {selectedPersonFailed ? "Couldn't load this person." : <Spinner size="lg" />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <CreateEmployeeDialog open={createEmployeeOpen} onOpenChange={setCreateEmployeeOpen} />
    </div>
  )
}

export default PeopleContent
